<template>
  <v-card :key="productKey" flat>
    <v-layout v-if="expandedProduct" wrap border-bottom>
      <v-flex v-if="shouldShowTabs" xs12>
        <v-tabs v-model="activeTab" background-color="transparent" class="expanded-product-tabs">
          <v-tab v-for="tsgKey in toolStoreGroupKeys" :key="tsgKey" @click="onTabChange(tsgKey)">
            {{ getToolStoreGroupDescription(tsgKey) }}
          </v-tab>
        </v-tabs>
      </v-flex>

      <v-layout wrap class="position-relative">
        <v-overlay :value="isFetchingToolStoreGroup" absolute opacity="0.1">
          <v-progress-circular indeterminate size="64" width="4" color="primary" />
        </v-overlay>
        <v-flex xs9 border-bottom pt-2>
          <v-layout wrap>
            <v-flex xs12>
              <price-history-chart
                :key="
                  `${activeToolStoreGroupKey}-${isFetchingToolStoreGroup ? 'loading' : 'loaded'}`
                "
                :price-history="addChartCoordinates(activeProduct.priceHistoryWeek)"
                :scenario-price="
                  activeProduct.scenarioResults ? activeProduct.scenarioResults.scenarioPrice : {}
                "
                :scenario-cost="activeProduct.scenarioCost"
                :cost-history="addChartCoordinates(activeProduct.costHistoryWeek)"
                :competitors="activeProduct.competitor"
                :competitor-names-map="competitorDescriptionToDisplayDescriptionMap"
              />
            </v-flex>
          </v-layout>
          <v-layout border-top>
            <v-flex xs6>
              <penalty-functions
                :scenario-results="activeProduct.scenarioResults"
                :product-key="productKey"
                :scenario-key="scenarioKey"
                :tool-store-group-key="activeToolStoreGroupKey"
                :competitor-names-map="competitorDescriptionToDisplayDescriptionMap"
                :warnings="productWarnings"
                @goToPenaltyRule="goToPenaltyRule"
              />
            </v-flex>
            <v-flex xs6 border-left pa-2>
              <h3>{{ $t('gridView.settings') }}</h3>
              <p class="my-0">{{ $t('gridView.linksToSettings') }}:</p>
              <v-container pb-0 pt-2>
                <v-row class="goto-row-separator">
                  <h4>{{ $tc(`hierarchy.hierarchyLevels.${architectureGroupLevel}`, 1) }}</h4>
                </v-row>
                <v-row>
                  <v-col cols="8" class="py-1 px-0 d-flex align-center">
                    {{ architectureGroup.levelEntryDescription }}
                    <v-checkbox
                      v-model="isFilteringByArchitectureGroupEnabled"
                      prepend-icon="filter_list"
                      class="checkbox ml-2 pa-0"
                      hide-details
                      dense
                      :disabled="loading"
                      @click.prevent.stop="onToggleFilteringByArchitectureGroup"
                    />
                  </v-col>
                  <v-col cols="4" class="py-1 px-0 text-right">
                    <v-btn small class="ok-btn" @click="goToArchitectureGroup">
                      {{ $t('gridView.goToButtonText') }}
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row class="goto-row-separator">
                  <h4>{{ $t(`gridView.architectureDrivers`) }}</h4>
                </v-row>
                <v-row>
                  <v-col cols="8" class="py-1 px-0">
                    {{ /* Finding list relevant ADs is complicated. Compromise for now is to show AG name instead. */ }}
                    {{ architectureGroup.levelEntryDescription }}
                  </v-col>
                  <v-col cols="4" class="py-1 px-0 text-right">
                    <v-btn small class="ok-btn" @click="goToArchitectureDrivers">
                      {{ $t('gridView.goToButtonText') }}
                    </v-btn>
                  </v-col>
                </v-row>

                <feature-toggle
                  v-if="!isUnitManagerView"
                  :toggle="displayArchitectureSubGroupSplittingAttributes"
                >
                  <v-row class="goto-row-separator">
                    <h4>{{ $t(`gridView.architectureSubGroupSplittingAttributes`) }}</h4>
                  </v-row>
                  <v-row>
                    <v-col cols="8" class="py-1 px-0">
                      {{ formattedArchitectureSubGroupDescription }}
                    </v-col>
                    <v-col cols="4" class="py-1 px-0 text-right">
                      <v-btn
                        small
                        class="ok-btn"
                        @click="goToArchitectureSubGroupSplittingAttributes"
                      >
                        {{ $t('gridView.viewButtonText') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </feature-toggle>

                <v-row class="goto-row-separator">
                  <h4>{{ $t(`gridView.linePricingGroup`) }}</h4>
                </v-row>
                <v-row>
                  <v-col cols="8" class="py-1 px-0">
                    {{ linePricingGroup.linePricedGroupDescription }}
                  </v-col>
                  <v-col cols="4" class="py-1 px-0 text-right">
                    <v-btn small class="ok-btn" @click="goToLinePricingGroups">
                      {{ $t('gridView.goToButtonText') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs3 border-left border-bottom pt-2>
          <product-attributes :item="activeProduct" />
          <reference-prices
            v-if="isPriceOptimisationEnabled"
            class="mt-4"
            :data="referencePricesData"
          />
        </v-flex>
      </v-layout>
    </v-layout>
  </v-card>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { get, isEmpty, isNumber, isNil } from 'lodash';
import { pricingGroupLevel, architectureGroupLevel } from '@enums/hierarchy';
import {
  displayArchitectureSubGroupSplittingAttributes,
  useZones,
  useStoreGroupChains,
  enablePriceOptimisation,
  enableArchitectureLinePricingTabs,
} from '@enums/feature-flags';
import hierarchyFiltersMixin from '../../../mixins/hierarchyFilters';
import numberFormats from '@enums/number-formats';

export default {
  mixins: [hierarchyFiltersMixin],
  props: {
    product: {
      type: Object,
      required: true,
    },

    isUnitManagerView: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      architectureGroupLevel,
      displayArchitectureSubGroupSplittingAttributes,
      activeTab: 0,
      loadedToolStoreGroups: {},
      loadedLinePricingGroups: {},
      isFetchingToolStoreGroup: false,
    };
  },

  computed: {
    ...mapState('clientConfig', ['toggleLogic']),
    ...mapGetters('hierarchy', ['getHierarchy']),
    ...mapState('gridView', [
      'expandedProduct',
      'isFilteringByArchitectureGroupEnabled',
      'loading',
    ]),
    ...mapState('filters', ['selectedPriceType']),
    ...mapGetters('competitorMetadata', ['competitorDescriptionToDisplayDescriptionMap']),
    ...mapState('storeGroupRelationships', ['storegroupKeyNameMap']),

    productKey() {
      return String(this.product.productKey);
    },

    pricingGroupId() {
      return get(this.product, `hierarchy[${pricingGroupLevel}].id`);
    },

    architectureGroupId() {
      return (
        get(this.expandedProduct, `hierarchy[${architectureGroupLevel}]`) ||
        get(this.product, `hierarchy[${architectureGroupLevel}].id`)
      );
    },

    architectureSubGroupValues() {
      return get(this.product, 'subGroupValues', []);
    },

    scenarioKey() {
      return this.product.scenarioKey;
    },

    availableStoreGroupKeys() {
      if (!this.isGroupTsgsEnabled || isEmpty(this.storegroupKeyNameMap)) {
        return [];
      }

      const availableTsgKeys = get(this.product, 'availableProductTSGsKeys', []);
      if (!isEmpty(availableTsgKeys)) {
        return Object.keys(this.storegroupKeyNameMap).filter(key => availableTsgKeys.includes(key));
      }

      return Object.keys(this.storegroupKeyNameMap);
    },

    toolStoreGroupKey() {
      const tsgKey = get(this.product, 'toolStoreGroupKey', null);

      if (!tsgKey) {
        const storeGroupKeys = this.availableStoreGroupKeys;
        return storeGroupKeys.length > 0 ? storeGroupKeys[0] : null;
      }
      return tsgKey;
    },

    shouldShowTabs() {
      return this.isGroupTsgsEnabled && this.toolStoreGroupKeys.length > 1;
    },

    isGroupTsgsEnabled() {
      return this.toggleLogic[useZones] && this.toggleLogic[useStoreGroupChains];
    },

    toolStoreGroupKeys() {
      if (!isEmpty(this.availableStoreGroupKeys)) {
        return this.availableStoreGroupKeys;
      }

      return [this.toolStoreGroupKey];
    },

    activeToolStoreGroupKey() {
      if (!this.shouldShowTabs) {
        return this.toolStoreGroupKey;
      }

      return this.toolStoreGroupKeys[this.activeTab] || this.toolStoreGroupKey;
    },

    activeProduct() {
      if (!this.shouldShowTabs) {
        return this.expandedProduct;
      }

      const tsgKey = this.activeToolStoreGroupKey;
      return this.loadedToolStoreGroups[tsgKey] || this.expandedProduct;
    },

    architectureGroup() {
      return isEmpty(this.architectureGroupId)
        ? {
            levelEntryDescription: this.$t('gridView.noArchitectureGroup'),
            levelEntryKey: null,
          }
        : this.getHierarchy({
            levelEntryKey: this.architectureGroupId,
            level: architectureGroupLevel,
          })[0];
    },

    formattedArchitectureSubGroupDescription() {
      // treat empty string same as absence of data, replace with - manually.
      return this.architectureSubGroupValues === []
        ? '-'
        : this.architectureSubGroupValues
            .map(value => (isNumber(value) ? this.formatNumber({ number: value }) : value))
            .join('-');
    },

    linePricingGroup() {
      const tsgKey = this.activeToolStoreGroupKey;
      const cachedGroup = get(this.loadedLinePricingGroups, tsgKey);

      return !isEmpty(cachedGroup)
        ? cachedGroup
        : { linePricedGroupDescription: this.$t('gridView.noLinePricingGroup'), _id: null };
    },

    isPriceOptimisationEnabled() {
      return this.toggleLogic[enablePriceOptimisation];
    },

    referencePricesData() {
      const scenarioData = get(this.activeProduct, 'scenarioResults', {});
      const crossElasticityVolume = get(scenarioData, 'crossElasticityVolume', null);
      const strategicForecastVolume = get(scenarioData, 'strategicForecastVolume', null);

      return {
        strategic: {
          price: this.formatNumber({
            // displays optimized price, ignoring any overrides/locks
            number: get(scenarioData, 'optimizedPrice', null),
            format: numberFormats.priceFormat,
            nullAsDash: true,
          }),
          volume: this.formatNumber({
            number: !isNil(strategicForecastVolume) ? strategicForecastVolume / 1000 : null,
            format: numberFormats.oneDecimalPlace,
            nullAsDash: true,
          }),
        },
        crossElasticity: {
          price: this.formatNumber({
            number: get(scenarioData, 'crossElasticityPrice', null),
            format: numberFormats.priceFormat,
            nullAsDash: true,
          }),
          volume: this.formatNumber({
            number: !isNil(crossElasticityVolume) ? crossElasticityVolume / 1000 : null,
            format: numberFormats.oneDecimalPlace,
            nullAsDash: true,
          }),
        },
      };
    },

    productWarnings() {
      return get(this.activeProduct, 'scenarioResults.warnings', {});
    },
  },

  async created() {
    await this.fetchFullProductInfo({
      productKey: this.productKey,
      scenarioKey: this.scenarioKey,
      toolStoreGroupKey: this.toolStoreGroupKey,
    });

    // pricingGroup and scenario selected already by the time this component is mounted. Just need to set AG based on product.
    this.setSelectedFilter({
      filterName: 'architectureGroup',
      filterValue: this.architectureGroupId,
    });
    this.setSelectedFilter({
      filterName: 'scenario',
      filterValue: this.scenarioKey,
    });

    const linePricingGroupForProduct = await this.getLinePricingGroupForProduct({
      productKey: this.productKey,
      toolStoreGroupKey: this.toolStoreGroupKey,
    });

    if (this.expandedProduct) {
      // Cache the initial tool store group data
      this.loadedToolStoreGroups[this.toolStoreGroupKey] = this.expandedProduct;
      this.$set(this.loadedLinePricingGroups, this.toolStoreGroupKey, linePricingGroupForProduct);

      // Set active tab based on the selected tool store group
      this.initializeActiveTab();
    }
  },

  destroyed() {
    this.setExpandedProduct(null);
  },

  async beforeDestroy() {
    // vuex only reads from local storage on page load, so state is inconsistent across tabs. with this hook, the state is reset when the component is closed.
    // e.g. in the current tab newTabOpenArchitectureRules may be true, so it needs to be explicitly set to false.
    // e.g. set Workpackage, open a new tab and change it. The old tab will have the old workpackage until the page is reloaded.
    await Promise.all([
      this.resetNewTabFilterState(),
      this.resetNewTabState(),
      this.resetRulesNewTabState(),
    ]);
  },

  methods: {
    ...mapActions('architectureGroup', [
      'getLinePricingGroupForProduct',
      'setNewTabOpenLinePricingRules',
      'setNewTabOpenArchitectureRules',
      'setNewTabActiveLinePricingGroupIndex',
      'setNewTabOpenArchitectureSubGroupSplittingAttributes',
      'resetNewTabState',
    ]),
    ...mapActions('rules', ['setNewTabOpenPenaltyRule']),
    ...mapActions('rules', {
      resetRulesNewTabState: 'resetNewTabState',
    }),
    ...mapActions('architectureDrivers', ['fetchArchitectureDrivers']),
    ...mapActions('filters', ['setSelectedFilter', 'resetNewTabFilterState']),
    ...mapActions('gridView', ['fetchFullProductInfo', 'setExpandedProduct']),

    // Initialize the active tab based on the tool store group that was clicked in the grid
    initializeActiveTab() {
      if (!this.shouldShowTabs) {
        return;
      }

      const selectedIndex = this.toolStoreGroupKeys.findIndex(
        key => key === this.toolStoreGroupKey
      );
      this.activeTab = selectedIndex >= 0 ? selectedIndex : 0;
    },

    // Get description for a tool store group by key
    getToolStoreGroupDescription(tsgKey) {
      return get(this.storegroupKeyNameMap, tsgKey, tsgKey);
    },

    // Handle tab change
    async onTabChange(tsgKey) {
      // If we already have this tool store group data loaded, use it
      if (this.loadedToolStoreGroups[tsgKey]) {
        return;
      }

      // Otherwise, fetch data for this tool store group
      await this.fetchToolStoreGroupData(tsgKey);
    },

    // Fetch data for a specific tool store group
    async fetchToolStoreGroupData(tsgKey) {
      if (!tsgKey || this.isFetchingToolStoreGroup) {
        return;
      }

      try {
        this.isFetchingToolStoreGroup = true;

        // Fetch detailed product info for this tool store group
        await this.fetchFullProductInfo({
          productKey: this.productKey,
          scenarioKey: this.scenarioKey,
          toolStoreGroupKey: tsgKey,
        });

        this.loadedToolStoreGroups[tsgKey] = this.expandedProduct;

        const linePricingGroupForProduct = await this.getLinePricingGroupForProduct({
          productKey: this.productKey,
          toolStoreGroupKey: tsgKey,
        });

        this.$set(this.loadedLinePricingGroups, tsgKey, linePricingGroupForProduct);
      } catch (error) {
        console.error('Error loading tool store group data:', error);
      } finally {
        this.isFetchingToolStoreGroup = false;
      }
    },

    async setNewTabFilters() {
      // set the filter so it's there when opening new tabs.
      await Promise.all([
        this.setSelectedFilter({
          filterName: 'newTabArchitectureGroup',
          filterValue: this.architectureGroupId,
        }),
        this.setSelectedFilter({
          filterName: 'newTabScenario',
          filterValue: this.scenarioKey,
        }),
        this.setSelectedFilter({
          filterName: 'newTabPricingGroup',
          filterValue: this.pricingGroupId,
        }),
        this.setSelectedFilter({
          filterName: 'newTabPriceType',
          filterValue: this.selectedPriceType,
        }),
        // to ensure filter state is clean. previous issue found as part of PRICE-1969.
        // opening architecture group first causes empty filter to be added on LPG screen, due to invalid AG filter applied in filter-panel.
        this.setSelectedFilter({
          filterName: 'newTabArchitectureGroupAttributeFilter',
          filterValue: [],
        }),
      ]);
    },

    addChartCoordinates(data) {
      return (data || []).map(h => ({
        ...h,
        x: h.effectiveTimestamp,
        y: h.price,
      }));
    },

    async goToArchitectureGroup() {
      // only need newTabArchitectureGroupAttributeFilter when going to attributes page, for attribute-filter-panel.vue.
      await Promise.all([
        this.setNewTabFilters(),
        this.setNewTabOpenLinePricingRules(false),
        this.setNewTabOpenArchitectureRules(false),
        this.setNewTabOpenArchitectureSubGroupSplittingAttributes(null),
        // avoid interactions with other newtab filters by resetting state in vuex / local storage above
        this.setSelectedFilter({
          filterName: 'newTabArchitectureGroupAttributeFilter',
          filterValue: this.architectureGroupId,
        }),
      ]);

      const route = this.$router.resolve({
        path: `/inputs/attributes/workpackage/${this.$route.params.workpackageId}`,
      });
      window.open(route.href, '_blank');
    },

    async goToLinePricingGroups() {
      let activeLinePricingGroupIndex = get(this.loadedLinePricingGroups, [
        this.activeToolStoreGroupKey,
        'linePricingGroupIndex',
      ]);

      if (isNil(activeLinePricingGroupIndex) || activeLinePricingGroupIndex < 0) {
        activeLinePricingGroupIndex = null;
      }

      await Promise.all([
        this.setNewTabFilters(),
        this.setNewTabOpenLinePricingRules(true),
        this.setNewTabOpenArchitectureRules(false),
        this.setNewTabActiveLinePricingGroupIndex(activeLinePricingGroupIndex), // only needed to expand specific LPG
        this.setNewTabOpenArchitectureSubGroupSplittingAttributes(null),
      ]);
      const path = this.toggleLogic[enableArchitectureLinePricingTabs]
        ? `/settings/architecture-v2/workpackage/${
            this.$route.params.workpackageId
          }/line-pricing-rules`
        : `/settings/architecture/workpackage/${this.$route.params.workpackageId}`;

      const route = this.$router.resolve({ path });
      window.open(route.href, '_blank');
    },

    async goToArchitectureDrivers() {
      await Promise.all([
        this.setNewTabFilters(),
        this.setNewTabOpenLinePricingRules(false),
        this.setNewTabOpenArchitectureRules(true),
        this.setNewTabOpenArchitectureSubGroupSplittingAttributes(null),
      ]);

      const path = this.toggleLogic[enableArchitectureLinePricingTabs]
        ? `/settings/architecture-v2/workpackage/${
            this.$route.params.workpackageId
          }/architecture-rules`
        : `/settings/architecture/workpackage/${this.$route.params.workpackageId}`;

      const route = this.$router.resolve({ path });
      window.open(route.href, '_blank');
    },

    // different to other goTo funcs, sets ag-grid filter for v2-inputs screens only
    async goToArchitectureSubGroupSplittingAttributes() {
      await Promise.all([
        this.setNewTabFilters(),
        this.setNewTabOpenLinePricingRules(false),
        this.setNewTabOpenArchitectureRules(false),
        // productKey tsgKey set so ag-grid can load attribute data for this product,
        // then filter by sub group splitting attribute values
        this.setNewTabOpenArchitectureSubGroupSplittingAttributes(
          this.formattedArchitectureSubGroupDescription
        ),
        this.setSelectedFilter({
          filterName: 'newTabArchitectureGroupAttributeFilter',
          filterValue: this.architectureGroupId,
        }),
      ]);

      const route = this.$router.resolve({
        path: `/inputs/attributes/workpackage/${this.$route.params.workpackageId}`,
      });
      window.open(route.href, '_blank');
    },

    async onToggleFilteringByArchitectureGroup() {
      this.$emit('toggleFilteringByArchitectureGroup', {
        isFilterEnabled: !!this.isFilteringByArchitectureGroupEnabled,
        architectureGroupAttribute: this.getArchitectureGroupFilterDefaults(),
        architectureGroupId: this.architectureGroupId,
      });
    },

    /** Opens the penalties settings page in a new tab with the specified rule expanded and its penalty type tab selected. */
    async goToPenaltyRule({ ruleName, penaltyCategory }) {
      await this.setNewTabFilters();
      await this.setNewTabOpenPenaltyRule({ ruleName, penaltyCategory });
      const route = this.$router.resolve({
        path: `/settings/penalties/workpackage/${this.$route.params.workpackageId}`,
      });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.border-top {
  border-top: 0.1rem solid $pricing-grey;
}

.border-left {
  border-left: 0.1rem solid $pricing-grey;
}

.border-bottom {
  border-bottom: 0.1rem solid $pricing-grey;
}

.block {
  margin: 50px;
}

.goto-row-separator {
  border-bottom: 0.1rem solid $pricing-grey;
}

// TODO: move all .ok-btn classes into main.scss? Same class in 5 components now.
.row .col .ok-btn {
  color: $pricing-white;
  background-color: $btn-ok-color !important;
  border-color: $btn-ok-color;
}

.checkbox::v-deep {
  $scale: 0.65;
  transform: scale($scale);
  transform-origin: left;

  .v-input__prepend-outer {
    margin-right: 0;
  }
}

.expanded-product-tabs {
  width: 100%;
  border-bottom: 0.1rem solid $pricing-grey;

  ::v-deep {
    .v-tabs-bar {
      height: 2.5rem;

      .v-tab {
        text-transform: none;
        font-size: 14px;
        font-weight: 400;
        min-width: 110px;
        background-color: #f2f4f8;
        margin-right: 1rem;
        border-right: 0.1rem solid $pricing-grey;
        border-top: 0.1rem solid $pricing-grey;
        border-left: 0.3rem solid $tab-border-blue-active;
        border-bottom: none;

        &--active {
          color: $pricing-primary;
        }
      }

      .v-tab--active {
        background-color: #d7dee7;
        color: $pricing-primary;
        font-weight: 700;
      }

      .v-tabs-slider-wrapper {
        height: 0.1rem !important;
        .v-tabs-slider {
          background-color: transparent;
        }
      }
    }
  }
}
</style>
